import React, { useEffect, useRef, useState } from 'react';

import { ErrorBox } from './Loader.jsx';
import { notify } from '../lib/notifications';
import { PIN_MAX, PIN_MIN, pinProblem } from '../lib/orgDetailsLock.js';

/**
 * Super-admin dialog that sets or checks the organisation details PIN.
 *
 * Locking freezes the organisation record. It does not sign anyone out.
 */
export default function OrganisationDetailsLockModal({
  organisation,
  lockDetails,
  unlockDetails,
  onClose,
  onSaved,
}) {
  const locked = !!organisation?.details_locked;
  const [pin, setPin] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const firstField = useRef(null);
  const label = organisation?.name || organisation?.sub_domain || 'this organisation';

  useEffect(() => {
    firstField.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape' && !saving) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, saving]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    const problem = locked ? pinProblem(pin) : pinProblem(pin, confirmation);
    if (problem) {
      setError(problem);
      return;
    }
    setSaving(true);
    try {
      const updated = locked
        ? await unlockDetails(organisation.id, pin.trim())
        : await lockDetails(organisation.id, pin.trim(), confirmation.trim());
      notify.success(
        locked
          ? `${label} is unlocked. Its details can be edited or deleted.`
          : `${label} is locked. Its details stay frozen until this PIN is entered.`,
        { title: locked ? 'Unlocked' : 'Locked' },
      );
      onSaved(updated);
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="ty-modal-backdrop"
      onClick={() => { if (!saving) onClose(); }}
    >
      <form
        className="ty-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ty-org-lock-title"
        onClick={(e) => e.stopPropagation()}
        onSubmit={onSubmit}
      >
        <div className="ty-modal__head">
          <h3 id="ty-org-lock-title" className="ty-modal__title">
            {locked ? 'Unlock organisation' : 'Lock organisation'}
          </h3>
          <button
            type="button"
            className="ty-modal__close"
            onClick={onClose}
            disabled={saving}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div className="ty-modal__body">
          <p className="ty-muted" style={{ marginTop: 0 }}>
            {locked
              ? <>Enter the PIN to unlock <strong>{label}</strong>. Until then its details cannot be edited or deleted. People in the organisation can keep working.</>
              : <>Choose a PIN for <strong>{label}</strong>. While it is locked, its name, code, address, plan and status cannot be edited or deleted. People in the organisation can keep working.</>}
          </p>
          <ErrorBox error={error} />
          <div className="ty-field">
            <label htmlFor="ty-org-lock-pin">PIN *</label>
            <input
              ref={firstField}
              id="ty-org-lock-pin"
              type="password"
              className="ty-input"
              inputMode="numeric"
              autoComplete="off"
              name="org-details-pin"
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              minLength={PIN_MIN}
              maxLength={PIN_MAX}
              required
            />
          </div>
          {!locked && (
            <div className="ty-field">
              <label htmlFor="ty-org-lock-confirm">Confirm PIN *</label>
              <input
                id="ty-org-lock-confirm"
                type="password"
                className="ty-input"
                inputMode="numeric"
                autoComplete="off"
                name="org-details-pin-confirm"
                value={confirmation}
                onChange={(e) => setConfirmation(e.target.value)}
                minLength={PIN_MIN}
                maxLength={PIN_MAX}
                required
              />
            </div>
          )}
        </div>
        <div className="ty-modal__foot">
          <button
            type="button"
            className="ty-btn ty-btn--ghost"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>
          <button type="submit" className="ty-btn" disabled={saving}>
            {saving ? 'Saving…' : (locked ? 'Unlock' : 'Lock details')}
          </button>
        </div>
      </form>
    </div>
  );
}
